/**
 * A sidebar dos canais. Também mostra quem está dentro de cada canal de voz —
 * sem entrar. Ver a sala antes de entrar é meio caminho para as pessoas
 * entrarem: é o comportamento que o Discord acertou e que vale a pena copiar.
 */

import {
  type Channel,
  type Mute,
  type MuteScope,
  Permission,
  type PermissionBits,
  roomByVoiceName,
  type Snowflake,
  type VoiceState,
} from "@greenvoice/shared";
import { useEffect, useRef, useState } from "react";
import { useVoiceActions } from "../hooks/useVoice";
import { api } from "../lib/api";
import { useIsMobile } from "../lib/media";
import {
  can,
  channelTitle,
  channelTree,
  directChannels,
  isChannelMuted,
  isServerMuted,
  isTextual,
  isUnread,
  mentionsOf,
  muteOf,
  selfPermissions,
  threadsOf,
  voiceParticipants,
} from "../store/selectors";
import { muteKey, useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { ChannelCreate, ChannelSettings } from "./ChannelSettings";
import {
  IconBellOff,
  IconCamera,
  IconChevron,
  IconClose,
  IconHash,
  IconHeadphones,
  IconHeadphonesOff,
  IconLock,
  IconMembers,
  IconMic,
  IconMicOff,
  IconMore,
  IconPhoneOff,
  IconScreen,
  IconSettings,
  IconShield,
  IconSpeaker,
  IconThread,
  IconUserPlus,
} from "./Icons";
import { InvitePanel } from "./InvitePanel";
import { ServerSettings } from "./ServerSettings";
import { ThreadCreate } from "./ThreadCreate";

/**
 * As durações do silêncio. Quatro, e a última é «para sempre».
 *
 * São minutos e não datas de propósito: quem carimba a hora final é o servidor.
 * Um relógio adiantado neste browser dava um silêncio já expirado ao nascer, e
 * um atrasado dava um que nunca mais acabava.
 */
const MUTE_DURATIONS: { label: string; minutes: number | null }[] = [
  { label: "15 minutos", minutes: 15 },
  { label: "1 hora", minutes: 60 },
  { label: "8 horas", minutes: 480 },
  { label: "Até eu voltar a ligar", minutes: null },
];

/** «Silenciado até às 15:04» — a data só aparece se calhar noutro dia. */
function muteUntilLabel(mute: Mute): string {
  if (mute.until === null) return "Silenciado";
  const until = new Date(mute.until);
  const sameDay = until.toDateString() === new Date().toDateString();
  const hours = until.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Silenciado até às ${hours}`;
  return `Silenciado até ${until.toLocaleDateString("pt-PT", { day: "numeric", month: "long" })}, ${hours}`;
}

/**
 * A lista de durações, tal como aparece nos dois sítios que silenciam (a linha
 * do canal e o menu do servidor). Um componente só porque as duas listas têm de
 * oferecer exactamente o mesmo — duas cópias divergiam à primeira alteração.
 */
function MuteOptions({
  scope,
  targetId,
  flyout,
  onDone,
}: {
  scope: MuteScope;
  targetId: Snowflake;
  /** Ao lado (submenu do servidor) em vez de por baixo (sino de uma linha). */
  flyout?: boolean;
  onDone: () => void;
}) {
  const setMute = useStore((s) => s.setMute);
  return (
    <div className={`voice-menu${flyout ? " voice-menu--flyout" : ""}`} role="menu">
      {MUTE_DURATIONS.map((option) => (
        <button
          key={option.label}
          type="button"
          role="menuitem"
          className="voice-menu__item"
          onClick={() => {
            void setMute(scope, targetId, option.minutes).catch(() => undefined);
            onDone();
          }}
        >
          <span>{option.label}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * A coluna está recolhida?
 *
 * A pergunta é sempre DUAS: a escolha da pessoa (`sidebarCollapsed`, guardada
 * no browser) e se ela sequer se aplica. Num ecrã estreito esta coluna já é uma
 * gaveta — recolher uma gaveta que está fechada não quer dizer nada, e uma
 * gaveta ABERTA reduzida a 48px seria uma gaveta que se abre para não mostrar
 * nada. Por isso a largura manda sobre a preferência, e não o contrário.
 *
 * Vive aqui, exportada, porque é esta coluna que a resposta descreve — o
 * `AppShell` e o ecrã de chamada do escritório só precisam dela para pôr a
 * classe no invólucro, e uma segunda cópia da regra é uma divergência à espera
 * de acontecer.
 */
export function useSidebarCollapsed(): boolean {
  const collapsed = useStore((s) => s.sidebarCollapsed);
  // As duas leituras ANTES do `&&`, e não dentro dele: `collapsed &&
  // !useIsMobile()` só chamaria o segundo hook quando o primeiro fosse
  // verdadeiro, e um hook que aparece e desaparece rebenta o render («Rendered
  // more hooks than during the previous render») exactamente no clique que
  // recolhe a coluna.
  const mobile = useIsMobile();
  return collapsed && !mobile;
}

/**
 * A tira que fica no lugar da coluna: a seta, e mais nada.
 *
 * A seta NÃO desaparece com o que arrumou — a mesma lição da barra de ajuda do
 * escritório. Uma pega que se esconde com a gaveta obriga a adivinhar onde ela
 * foi parar, e ninguém adivinha duas vezes: fica na mesma linha do cabeçalho,
 * só a apontar para o outro lado.
 */
function SidebarCollapse({ collapsed }: { collapsed: boolean }) {
  const toggle = useStore((s) => s.toggleSidebar);
  const mobile = useIsMobile();
  const label = collapsed ? "Mostrar os canais" : "Recolher os canais";

  // Num ecrã estreito o botão não se esconde por estética: ali esta coluna é
  // uma gaveta e o `useSidebarCollapsed` ignora a preferência, portanto o botão
  // seria uma pega que não puxa nada — e uma pega que não faz nada é pior do
  // que pega nenhuma.
  if (mobile) return null;
  return (
    <button
      type="button"
      className="icon-button sidebar__collapse"
      onClick={toggle}
      title={label}
      aria-label={label}
      aria-expanded={!collapsed}
    >
      <IconChevron size={16} />
    </button>
  );
}

export function ChannelSidebar() {
  const state = useStore();
  const serverId = state.activeServerId;
  const server = serverId ? state.servers[serverId] : undefined;
  const collapsed = useSidebarCollapsed();

  // Calculado no render de propósito: `state` muda a cada evento, por isso um
  // `useMemo` sobre ele nunca acertaria na cache — seria custo sem ganho.
  const permissions = selfPermissions(state, serverId);
  const canManageServer =
    can(permissions, Permission.MANAGE_SERVER) ||
    can(permissions, Permission.MANAGE_ROLES) ||
    can(permissions, Permission.KICK_MEMBERS) ||
    can(permissions, Permission.BAN_MEMBERS) ||
    can(permissions, Permission.ASSIGN_ROLES);
  const canManageChannels = can(permissions, Permission.MANAGE_CHANNELS);
  const canRoles = can(permissions, Permission.MANAGE_ROLES);
  const canCreateInvites = can(permissions, Permission.CREATE_INVITES);
  // Já não depende de permissões: silenciar o servidor é preferência pessoal e
  // todos a têm. Antes, um membro sem cargo nenhum via só um `<h2>` morto — e
  // era exactamente essa pessoa, a que não manda no servidor, quem mais precisa
  // de o poder calar.
  const hasServerMenu = Boolean(server);

  const [invitePanel, setInvitePanel] = useState(false);
  // O estado vive na store: o cartão de perfil também abre esta janela, e é um
  // portal pendurado no `body` — não alcança nada que seja local daqui.
  const settingsTab = useStore((s) => s.serverSettingsTab);
  const setSettingsTab = useStore((s) => s.openServerSettings);
  const [createPanel, setCreatePanel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [muteOpen, setMuteOpen] = useState(false);
  const serverMute = serverId ? state.mutes[muteKey("SERVER", serverId)] : undefined;
  const serverMuted = serverId ? isServerMuted(state, serverId) : false;
  const groups = serverId ? channelTree(state, serverId) : [];
  const dms = directChannels(state);

  // Recolhida, esta coluna é a seta e mais nada. O que fica na tira não é
  // decidido aqui — a `VoiceBar` e o `UserPanel` são irmãos deste componente
  // (`AppShell`, `OfficeSpace`) e continuam montados: é a folha de estilo que
  // os aperta à largura da tira. É essa a diferença entre "arrumar a lista de
  // salas" e "ficar sem o botão de sair da chamada".
  if (collapsed) {
    return (
      <aside className="sidebar sidebar--mini">
        <header className="sidebar__head">
          <SidebarCollapse collapsed />
        </header>
      </aside>
    );
  }

  return (
    <aside className="sidebar">
      <header className="sidebar__head">
        {hasServerMenu ? (
          <div className="server-head">
            <button
              type="button"
              className={`server-head__name${menuOpen ? " is-open" : ""}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => {
                setMenuOpen((v) => !v);
                setMuteOpen(false);
              }}
              title={server?.name}
            >
              <span>{server?.name}</span>
              <IconChevron size={16} className="server-head__caret" />
            </button>
            {menuOpen ? (
              <>
                <button
                  type="button"
                  className="menu-scrim"
                  aria-hidden="true"
                  tabIndex={-1}
                  onClick={() => {
                    setMenuOpen(false);
                    setMuteOpen(false);
                  }}
                />
                <div className="server-menu voice-menu" role="menu">
                  {canManageServer ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="voice-menu__item"
                      onClick={() => {
                        setSettingsTab("overview");
                        setMenuOpen(false);
                      }}
                    >
                      <IconSettings size={15} />
                      <span>Configurações do servidor</span>
                    </button>
                  ) : null}
                  {canRoles ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="voice-menu__item"
                      onClick={() => {
                        setSettingsTab("roles");
                        setMenuOpen(false);
                      }}
                    >
                      <IconShield size={15} />
                      <span>Cargos e permissões</span>
                    </button>
                  ) : null}
                  {canCreateInvites ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="voice-menu__item"
                      onClick={() => {
                        setInvitePanel(true);
                        setMenuOpen(false);
                      }}
                    >
                      {/* Uma PESSOA com um mais, não o mesmo  de "criar
                          canal": dois itens do mesmo menu com o mesmo glifo
                          obrigam a ler o texto para os distinguir, e um menu
                          onde é preciso ler tudo é um menu sem ícones. */}
                      <IconUserPlus size={15} />
                      <span>Convidar pessoas</span>
                    </button>
                  ) : null}
                  {canManageChannels ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="voice-menu__item"
                      onClick={() => {
                        setCreatePanel(true);
                        setMenuOpen(false);
                      }}
                    >
                      <IconHash size={15} />
                      <span>Criar canal</span>
                    </button>
                  ) : null}
                  {/* Sem permissão nenhuma à frente: calar é preferência, não moderação. */}
                  {serverId && serverMuted ? (
                    <button
                      type="button"
                      role="menuitem"
                      className="voice-menu__item"
                      title={serverMute ? muteUntilLabel(serverMute) : undefined}
                      onClick={() => {
                        void state.clearMute("SERVER", serverId).catch(() => undefined);
                        setMenuOpen(false);
                      }}
                    >
                      <IconBellOff size={15} />
                      <span>Reativar notificações</span>
                    </button>
                  ) : null}
                  {serverId && !serverMuted ? (
                    <div
                      className="voice-menu__sub"
                      onMouseEnter={() => setMuteOpen(true)}
                      onMouseLeave={() => setMuteOpen(false)}
                    >
                      <button
                        type="button"
                        role="menuitem"
                        className={`voice-menu__item${muteOpen ? " is-active" : ""}`}
                        aria-haspopup="menu"
                        aria-expanded={muteOpen}
                        onClick={() => setMuteOpen((v) => !v)}
                      >
                        <IconBellOff size={15} />
                        <span>Silenciar servidor</span>
                        <IconChevron size={13} className="voice-menu__caret" />
                      </button>
                      {muteOpen ? (
                        <MuteOptions
                          scope="SERVER"
                          targetId={serverId}
                          flyout
                          onDone={() => {
                            setMuteOpen(false);
                            setMenuOpen(false);
                          }}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        ) : (
          <h2 title={server?.name ?? "Mensagens diretas"}>{server?.name ?? "Mensagens diretas"}</h2>
        )}
        {/*
         * Os quatro atalhos que aqui estavam — criar canal, cargos, definições,
         * convidar — saíram, e não se perde NADA: são um duplicado exacto do
         * menu que abre no nome do servidor, com as mesmas permissões a decidir
         * quem os vê. O raciocínio já estava escrito para o telemóvel; o que
         * mudou foi ele passar a valer também aqui.
         *
         * A conta: a coluna tem 248px e o cabeçalho gasta 24 de margem. Cinco
         * ícones (4 × 32 + espaços) comem 168 e não encolhem — ao nome sobravam
         * 48px, e "GreenVoice" aparecia como "G.". Já estava apertado com
         * quatro; a seta de recolher foi a gota. Entre um nome de servidor
         * legível e quatro botões repetidos, o nome ganha.
         *
         * E de caminho morre a confusão de terem duas setas iguais lado a lado:
         * o «Convidar» usava o mesmo chevron da seta de recolher — dois glifos
         * idênticos, duas acções que não têm nada que ver uma com a outra.
         */}
        <SidebarCollapse collapsed={false} />
      </header>

      {/*
       * O par de abas «Chat | Escritório» vivia aqui, entre o cabeçalho e a
       * lista. Saiu: esta coluna lê-se para escolher um CANAL, e um modo do
       * servidor no meio de uma lista de canais é uma coisa a mais para
       * atravessar antes de chegar ao que se veio buscar. A porta para o
       * escritório é agora um botão só, na barra fixa por cima de tudo
       * (`topbar__mode`, em `TopBar`): a mesma nas duas vistas, sempre no mesmo
       * pixel, e à vista mesmo com esta gaveta fechada no telemóvel.
       */}

      <div className="sidebar__scroll">
        {serverId === null ? (
          <>
            <button
              type="button"
              className={`sidebar__friends${state.activeChannelId === null ? " is-active" : ""}`}
              onClick={() => useStore.setState({ activeChannelId: null })}
            >
              <IconMembers size={18} />
              <span>Amigos</span>
            </button>
            {dms.length > 0 ? <h3 className="sidebar__label">Mensagens diretas</h3> : null}
            {dms.map((channel) => (
              <ChannelRow key={channel.id} channel={channel} />
            ))}
          </>
        ) : (
          groups.map((group) => (
            <CategoryGroup
              key={group.category?.id ?? "@orphans"}
              label={group.category?.name ?? null}
              channels={group.channels}
            />
          ))
        )}

        {serverId === null && dms.length === 0 ? <p className="sidebar__empty">Sem conversas ainda.</p> : null}
        {serverId !== null && groups.length === 0 ? (
          <p className="sidebar__empty">Este servidor não tem canais visíveis.</p>
        ) : null}
      </div>

      {invitePanel && serverId ? <InvitePanel serverId={serverId} onClose={() => setInvitePanel(false)} /> : null}
      {settingsTab && serverId ? (
        <ServerSettings serverId={serverId} initialTab={settingsTab} onClose={() => setSettingsTab(null)} />
      ) : null}
      {createPanel && serverId ? <ChannelCreate serverId={serverId} onClose={() => setCreatePanel(false)} /> : null}
    </aside>
  );
}

function CategoryGroup({ label, channels }: { label: string | null; channels: Channel[] }) {
  const [collapsed, setCollapsed] = useState(false);
  if (!channels.length) return null;

  return (
    <section className="category">
      {label ? (
        <button
          type="button"
          className={`category__head${collapsed ? " is-collapsed" : ""}`}
          onClick={() => setCollapsed((v) => !v)}
        >
          <IconChevron size={12} />
          <span>{label}</span>
        </button>
      ) : null}
      {collapsed ? null : channels.map((channel) => <ChannelRow key={channel.id} channel={channel} />)}
    </section>
  );
}

function ChannelRow({ channel }: { channel: Channel }) {
  const state = useStore();
  const voice = useVoiceActions();
  const [editing, setEditing] = useState(false);
  const [muteOpen, setMuteOpen] = useState(false);
  const [threading, setThreading] = useState(false);

  const permissions = selfPermissions(state, channel.serverId, channel);
  if (!can(permissions, Permission.VIEW_CHANNEL) && channel.serverId) return null;

  const canManage = Boolean(channel.serverId) && can(permissions, Permission.MANAGE_CHANNELS);
  const active = state.activeChannelId === channel.id;
  const unread = isUnread(state, channel);
  const mentions = mentionsOf(state, channel.id);
  const muted = isChannelMuted(state, channel);
  const mute = muteOf(state, channel);
  // Calado por arrasto do servidor inteiro: o sino fica a dizer porquê, mas
  // apagado. Carregar nele descalaria só este canal e a linha continuava muda —
  // um botão que não faz o que mostra é pior do que um botão que não se carrega.
  const inherited = mute?.scope === "SERVER";
  const isVoice = channel.type === "VOICE";
  const participants = isVoice ? voiceParticipants(state, channel.id) : [];
  const inThisChannel = state.voice?.channelId === channel.id;

  // A sala do escritório trancada por dentro fecha também esta linha. Sem isto,
  // a porta estava fechada no mapa e a mesma reunião ficava a um clique aqui ao
  // lado — a tranca só valia para quem se dava ao trabalho de lá ir a pé.
  //
  // Quem MANDA é o servidor (`voice.service.assertRoomUnlocked`, que reconta a
  // posição no Redis); isto é a metade honesta: mostra o cadeado e não gasta um
  // pedido que vai ser recusado. Ficar desactualizado — o `roomLocks` chega por
  // `SpaceState`, e quem nunca abriu o escritório nesta sessão não o tem — custa
  // no máximo um clique perdido, nunca uma entrada indevida.
  //
  // O `!inThisChannel` é a mesma excepção do servidor: quem já lá está dentro
  // não está a ENTRAR. É por ela que trancar a sala e depois carregar em
  // "Discord" não fecha a porta na cara de quem a trancou.
  const salaDaCall = isVoice && channel.serverId ? roomByVoiceName(channel.name) : null;
  const trancadaPor =
    salaDaCall && channel.serverId ? (state.roomLocks[channel.serverId]?.[salaDaCall.id] ?? null) : null;
  const trancada = trancadaPor !== null && !inThisChannel;

  // Só onde um tópico pode nascer: dentro de outro tópico não, num DM também
  // não (não há lá permissões que o segurem). `CREATE_THREADS` é um direito de
  // membro e não de moderador — é essa a diferença para a roda dentada ao lado.
  const threadable = channel.type === "TEXT" || channel.type === "ANNOUNCEMENT";
  const canThread = threadable && Boolean(channel.serverId) && can(permissions, Permission.CREATE_THREADS);
  const threads = threadable ? threadsOf(state, channel.id) : [];

  const open = () => {
    if (isVoice) {
      // Seleccionar continua a passar: ver a linha não é entrar na sala. O que
      // a tranca corta é a ligação — e corta-se aqui só para não pedir o que
      // seria recusado; a recusa que conta é a do servidor.
      state.selectChannel(channel.id);
      if (!trancada && !inThisChannel && can(permissions, Permission.CONNECT)) void voice.join(channel.id);
      return;
    }
    state.selectChannel(channel.id);
  };

  return (
    <div className="channel">
      <button
        type="button"
        className={`channel__row${active ? " is-active" : ""}${unread && !active ? " is-unread" : ""}${
          muted ? " is-muted" : ""
        }${trancada ? " is-locked" : ""}`}
        onClick={open}
        title={
          trancada
            ? `${channel.name} está trancada por dentro. Bate à porta no escritório.`
            : (channel.topic ?? undefined)
        }
      >
        <span className="channel__icon">
          {isVoice ? <IconSpeaker size={17} /> : isTextual(channel) ? <IconHash size={17} /> : null}
        </span>
        <span className="channel__name">{channel.serverId ? channel.name : channelTitle(state, channel)}</span>
        {/* O cadeado fica DENTRO da linha e não no `channel__actions`: aquilo
            aparece ao passar o rato (são acções à espera do rato) e isto é
            estado — tem de se ver sem lá ir. */}
        {trancada ? (
          <span className="channel__lock" aria-label="Sala trancada">
            <IconLock size={13} />
          </span>
        ) : null}
        {isVoice && channel.userLimit > 0 ? (
          <span className="channel__limit">
            {participants.length}/{channel.userLimit}
          </span>
        ) : null}
        {mentions > 0 ? <span className="channel__badge">{mentions > 99 ? "99+" : mentions}</span> : null}
      </button>

      {/*
       * Sino e roda dentada juntos, sobrepostos à direita da linha: estão fora
       * do `channel__row` porque um `<button>` dentro de outro é HTML inválido,
       * e o `stopPropagation` impede que carregar neles abra o canal.
       *
       * O sino não passa por permissão nenhuma, ao contrário da roda dentada ao
       * lado: editar o canal é mandar nele, calá-lo é só não querer ouvi-lo.
       */}
      <div className="channel__actions">
        {isTextual(channel) ? (
          <div className="channel__mute">
            <button
              type="button"
              className={`channel__bell${muted ? " is-on" : ""}`}
              disabled={inherited}
              aria-haspopup={muted ? undefined : "menu"}
              aria-expanded={muted ? undefined : muteOpen}
              title={
                inherited
                  ? "Silenciado por o servidor inteiro estar silenciado"
                  : mute
                    ? muteUntilLabel(mute)
                    : "Silenciar"
              }
              onClick={(e) => {
                e.stopPropagation();
                if (muted) {
                  void state.clearMute("CHANNEL", channel.id).catch(() => undefined);
                  return;
                }
                setMuteOpen((v) => !v);
              }}
            >
              <IconBellOff size={14} />
            </button>
            {muteOpen ? (
              <>
                <button
                  type="button"
                  className="menu-scrim"
                  aria-hidden="true"
                  tabIndex={-1}
                  onClick={() => setMuteOpen(false)}
                />
                <MuteOptions scope="CHANNEL" targetId={channel.id} onDone={() => setMuteOpen(false)} />
              </>
            ) : null}
          </div>
        ) : null}

        {canThread ? (
          <button
            type="button"
            className="channel__gear"
            title={`Abrir tópico em #${channel.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setThreading(true);
            }}
          >
            <IconThread size={14} />
          </button>
        ) : null}

        {canManage ? (
          <button
            type="button"
            className="channel__gear"
            title={`Editar #${channel.name}`}
            onClick={(e) => {
              e.stopPropagation();
              setEditing(true);
            }}
          >
            <IconSettings size={14} />
          </button>
        ) : null}
      </div>

      {editing ? <ChannelSettings channelId={channel.id} onClose={() => setEditing(false)} /> : null}
      {threading ? <ThreadCreate channelId={channel.id} onClose={() => setThreading(false)} /> : null}

      {participants.length > 0 ? (
        <ul className="channel__voice">
          {participants.map((participant) => (
            <VoiceMemberRow
              key={participant.userId}
              participant={participant}
              channel={channel}
              permissions={permissions}
            />
          ))}
        </ul>
      ) : null}

      {/*
       * Os tópicos pendurados no canal de onde saíram, e só quando existem: um
       * cabeçalho "Tópicos" permanente seria ruído em todos os servidores que
       * nunca abrem nenhum. Quem os desenha é esta linha e não a `channelTree`,
       * porque o `parentId` de um tópico aponta para aqui e não para a categoria.
       */}
      {threads.length > 0 ? (
        <ul className="channel__threads">
          {threads.map((thread) => (
            <ThreadRow key={thread.id} thread={thread} permissions={permissions} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Uma linha de tópico, por baixo do canal-pai.
 *
 * Não repete o sino nem a roda dentada da linha de cima. Silenciar herda-se do
 * pai (o `isChannelMuted` sobe ao servidor, e o tópico vive dentro do canal que
 * já se calou); editar um tópico é mudar-lhe o nome, e para isso existe fechá-lo
 * e abrir outro. O que fica é o essencial: entrar, ver que tem por ler, e fechar.
 */
function ThreadRow({ thread, permissions }: { thread: Channel; permissions: PermissionBits }) {
  const state = useStore();
  const [closing, setClosing] = useState(false);

  const active = state.activeChannelId === thread.id;
  const unread = isUnread(state, thread);
  const mentions = mentionsOf(state, thread.id);
  // Quem abriu o tópico pode fechá-lo sem ser moderador — é a razão de a coluna
  // `ownerId` existir. O servidor volta a verificar isto; aqui só se esconde o
  // botão a quem levaria 403.
  const canClose = state.self?.id === thread.ownerId || can(permissions, Permission.MANAGE_THREADS);

  return (
    <li className="channel__thread">
      <button
        type="button"
        className={`channel__thread-row${active ? " is-active" : ""}${unread && !active ? " is-unread" : ""}`}
        onClick={() => state.selectChannel(thread.id)}
        title={thread.name}
      >
        <IconThread size={13} />
        <span className="channel__thread-name">{thread.name}</span>
        {mentions > 0 ? <span className="channel__badge">{mentions > 99 ? "99+" : mentions}</span> : null}
      </button>

      {canClose ? (
        <button
          type="button"
          className="channel__thread-close"
          disabled={closing}
          title="Fechar tópico"
          onClick={(e) => {
            e.stopPropagation();
            setClosing(true);
            // Sai daqui pelo `ChannelDelete` do gateway, que também devolve quem
            // lá estivesse ao canal-pai. Falhando, destranca-se o botão: uma
            // linha que ficou e um botão morto era pior do que poder tentar.
            void api.channels.remove(thread.id).catch(() => setClosing(false));
          }}
        >
          <IconClose size={12} />
        </button>
      ) : null}
    </li>
  );
}

/**
 * Uma pessoa dentro de uma sala de voz, com o menu de moderação (⋯) para quem
 * tem permissão. Silenciar/ensurdecer à força, mover para outro canal e
 * desligar da sala são acções de servidor — a API re-verifica permissão e
 * hierarquia, aqui só escondemos o que o moderador não pode fazer. As acções
 * devolvem 204: o estado real chega pelo gateway, por isso disparamos e fechamos.
 */
function VoiceMemberRow({
  participant,
  channel,
  permissions,
}: {
  participant: VoiceState;
  channel: Channel;
  permissions: PermissionBits;
}) {
  const state = useStore();
  const user = state.users[participant.userId];
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setMoveOpen(false);
      }
    };
    document.addEventListener("mousedown", close, true);
    return () => document.removeEventListener("mousedown", close, true);
  }, [menuOpen]);

  if (!user) return null;

  const isSelf = state.self?.id === participant.userId;
  const canMove = can(permissions, Permission.MOVE_MEMBERS);
  const canMute = can(permissions, Permission.MUTE_MEMBERS);
  const canDeafen = can(permissions, Permission.DEAFEN_MEMBERS);
  const canModerate = Boolean(channel.serverId) && !isSelf && (canMove || canMute || canDeafen);

  // Destinos do "mover para": os outros canais de voz do mesmo servidor.
  const moveTargets = canMove
    ? Object.values(state.channels).filter(
        (c) => c.type === "VOICE" && c.serverId === channel.serverId && c.id !== channel.id,
      )
    : [];

  const act = (run: () => Promise<unknown>) => {
    void run().catch(() => undefined);
    setMenuOpen(false);
    setMoveOpen(false);
  };

  return (
    <li className="channel__voice-item">
      <Avatar user={user} size={20} />
      <span className="channel__voice-name">{user.displayName ?? user.username}</span>
      {participant.selfMute || participant.serverMute ? <IconMicOff size={13} className="channel__voice-flag" /> : null}
      {/* Próprio E imposto, como no microfone da linha de cima: quem olha quer
          saber se a pessoa OUVE, não quem lhe cortou o som. Faltava o próprio —
          a lista de membros e os bonecos do escritório já os tratam aos dois. */}
      {participant.selfDeaf || participant.serverDeaf ? (
        <IconHeadphonesOff size={13} className="channel__voice-flag" />
      ) : null}
      {participant.screenSharing ? <IconScreen size={13} className="channel__voice-flag is-live" /> : null}
      {participant.cameraOn ? <IconCamera size={13} className="channel__voice-flag is-live" /> : null}

      {canModerate ? (
        <div className="channel__voice-menu" ref={menuRef}>
          <button
            type="button"
            className="channel__voice-mod"
            title="Moderar"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <IconMore size={14} />
          </button>
          {menuOpen ? (
            <div className="voice-menu" role="menu">
              {canMute ? (
                <button
                  type="button"
                  role="menuitem"
                  className="voice-menu__item"
                  onClick={() =>
                    act(() => api.voice.moderate(participant.userId, { serverMute: !participant.serverMute }))
                  }
                >
                  {participant.serverMute ? <IconMic size={15} /> : <IconMicOff size={15} />}
                  <span>{participant.serverMute ? "Dessilenciar" : "Silenciar"}</span>
                </button>
              ) : null}
              {canDeafen ? (
                <button
                  type="button"
                  role="menuitem"
                  className="voice-menu__item"
                  onClick={() =>
                    act(() => api.voice.moderate(participant.userId, { serverDeaf: !participant.serverDeaf }))
                  }
                >
                  {participant.serverDeaf ? <IconHeadphones size={15} /> : <IconHeadphonesOff size={15} />}
                  <span>{participant.serverDeaf ? "Desensurdecer" : "Ensurdecer"}</span>
                </button>
              ) : null}
              {canMove && moveTargets.length > 0 ? (
                <div
                  className="voice-menu__sub"
                  onMouseEnter={() => setMoveOpen(true)}
                  onMouseLeave={() => setMoveOpen(false)}
                >
                  <button
                    type="button"
                    role="menuitem"
                    className={`voice-menu__item${moveOpen ? " is-active" : ""}`}
                    aria-haspopup="menu"
                    aria-expanded={moveOpen}
                    onClick={() => setMoveOpen((v) => !v)}
                  >
                    <IconSpeaker size={15} />
                    <span>Mover para</span>
                    <IconChevron size={13} className="voice-menu__caret" />
                  </button>
                  {moveOpen ? (
                    <div className="voice-menu voice-menu--flyout" role="menu">
                      {moveTargets.map((target) => (
                        <button
                          key={target.id}
                          type="button"
                          role="menuitem"
                          className="voice-menu__item"
                          onClick={() => act(() => api.voice.move(participant.userId, target.id))}
                        >
                          <IconSpeaker size={15} />
                          <span>{target.name}</span>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}
              {canMove ? (
                <button
                  type="button"
                  role="menuitem"
                  className="voice-menu__item voice-menu__item--danger"
                  onClick={() => act(() => api.voice.disconnect(participant.userId))}
                >
                  <IconPhoneOff size={15} />
                  <span>Desligar da sala</span>
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
